import type { BarInterval, RangeKey } from "./market.js";

/**
 * Earliest date the Alpaca free plan provides historical data for.
 * Used so that "MAX" means "everything we can actually fetch" rather than the
 * security's full lifetime.
 */
export const DATA_START_ISO = "2016-01-01T00:00:00Z";

export interface RangeSpec {
  /** Bar aggregation to request from the provider. */
  interval: BarInterval;
  /** Start of the window as UTC epoch ms. */
  start: number;
  /** End of the window as UTC epoch ms. */
  end: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Map a user-facing range to a provider bar interval plus an absolute time
 * window. `now` is injectable so the mapping is deterministic and testable.
 */
export function resolveRange(range: RangeKey, now: number = Date.now()): RangeSpec {
  const end = now;
  const dataStart = Date.parse(DATA_START_ISO);

  switch (range) {
    case "1D":
      return { interval: "5Min", start: end - 1 * DAY, end };
    case "5D":
      return { interval: "15Min", start: end - 5 * DAY, end };
    case "1M":
      return { interval: "1Hour", start: end - 30 * DAY, end };
    case "3M":
      return { interval: "1Day", start: end - 90 * DAY, end };
    case "6M":
      return { interval: "1Day", start: end - 182 * DAY, end };
    case "YTD":
      return { interval: "1Day", start: startOfYear(end), end };
    case "1Y":
      return { interval: "1Day", start: end - 365 * DAY, end };
    case "5Y":
      return { interval: "1Week", start: end - 5 * 365 * DAY, end };
    case "MAX":
      return { interval: "1Month", start: dataStart, end };
    default: {
      const _exhaustive: never = range;
      throw new Error(`Unhandled range: ${String(_exhaustive)}`);
    }
  }
}

/** Resolve a fixed-size page ending immediately before an existing data set. */
export function resolveHistoricalRange(
  range: RangeKey,
  before: number,
): RangeSpec {
  if (range === "MAX") return resolveRange(range, before);

  const dataStart = Date.parse(DATA_START_ISO);
  const duration = historicalPageDuration(range);
  return {
    interval: resolveRange(range, before).interval,
    start: Math.max(dataStart, before - duration),
    end: before,
  };
}

/** True when a range uses an intraday interval (minutes/hours). */
export function isIntradayRange(range: RangeKey): boolean {
  const { interval } = resolveRange(range, 0);
  return (
    interval === "1Min" ||
    interval === "5Min" ||
    interval === "15Min" ||
    interval === "30Min" ||
    interval === "1Hour"
  );
}

/**
 * How long a bars response for this range can be cached server-side (ms).
 * Intraday data changes frequently; long-horizon daily/weekly data barely
 * changes intraday, so we can cache it much longer.
 */
export function barsCacheTtlMs(range: RangeKey): number {
  return isIntradayRange(range) ? 30_000 : 5 * MINUTE;
}

function startOfYear(epochMs: number): number {
  const d = new Date(epochMs);
  return Date.UTC(d.getUTCFullYear(), 0, 1);
}

function historicalPageDuration(range: Exclude<RangeKey, "MAX">): number {
  switch (range) {
    case "1D":
      return DAY;
    case "5D":
      return 5 * DAY;
    case "1M":
      return 30 * DAY;
    case "3M":
      return 90 * DAY;
    case "6M":
      return 182 * DAY;
    case "YTD":
    case "1Y":
      return 365 * DAY;
    case "5Y":
      return 5 * 365 * DAY;
    default: {
      const _exhaustive: never = range;
      throw new Error(`Unhandled range: ${String(_exhaustive)}`);
    }
  }
}
